// src/components/Header.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../config";

// Importujemy obrazy z src/assets/index.js
import {
  logoLight,
  logoDark,
  moonIcon,
  sunIcon,
  userLight,
  userDark,
  Powiadomienie0,
  Powiadomienie1,
  Powiadomienie2,
  Powiadomienie3,
  Powiadomienie4,
  Powiadomienie5,
  deleteOn, // ✅ dodaj export w assets/index.js
} from "../assets";

function Header({
  isAuthenticated,
  currentUser,
  onLogout,
  toggleTheme,
  theme,
  showUserMenu,
  setShowUserMenu,
  goToProfile,
  goToSettings,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const dropdownRef = useRef(null); // user menu
  const notificationsRef = useRef(null); // notifications menu

  const isOnDashboard = location.pathname === "/dashboard";

  // --- Notifications state ---
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsMenu, setShowNotificationsMenu] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const getBellIcon = useCallback(() => {
    if (unreadCount >= 5) return Powiadomienie5;
    if (unreadCount === 4) return Powiadomienie4;
    if (unreadCount === 3) return Powiadomienie3;
    if (unreadCount === 2) return Powiadomienie2;
    if (unreadCount === 1) return Powiadomienie1;
    return Powiadomienie0;
  }, [unreadCount]);

  // ------------------------------
  // API CALLS
  // ------------------------------
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/notifications/unread-count`, {
        method: "GET",
        credentials: "include",
      });

      if (res.status === 401) {
        setUnreadCount(0);
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        console.error("unread-count nie OK:", res.status, text);
        return;
      }

      const data = await res.json();
      setUnreadCount(Number(data?.count || 0));
    } catch (err) {
      console.error("Błąd pobierania unread-count:", err);
    }
  }, [isAuthenticated]);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }

    setLoadingNotifications(true);

    try {
      const res = await fetch(`${API_URL}/notifications?limit=30&offset=0`, {
        method: "GET",
        credentials: "include",
      });

      if (res.status === 401) {
        setNotifications([]);
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        console.error("notifications list nie OK:", res.status, text);
        return;
      }

      const data = await res.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Błąd pobierania listy powiadomień:", err);
    } finally {
      setLoadingNotifications(false);
    }
  }, [isAuthenticated]);

  const markNotificationRead = useCallback(
    async (notificationId) => {
      try {
        const res = await fetch(`${API_URL}/notifications/${notificationId}/read`, {
          method: "PATCH",
          credentials: "include",
        });

        if (!res.ok) {
          const text = await res.text();
          console.error("PATCH read nie OK:", res.status, text);
          return;
        }

        // Jeżeli backend zwróci read_at, można użyć do synchro
        // const data = await res.json();

      } catch (err) {
        console.error("Błąd oznaczania powiadomienia jako read:", err);
      } finally {
        // ✅ najpewniejsze: odśwież licznik z backendu
        fetchUnreadCount();
      }
    },
    [fetchUnreadCount]
  );

  const markAllRead = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/notifications/read-all`, {
        method: "PATCH",
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("PATCH read-all nie OK:", res.status, text);
        return;
      }

      // ✅ optymistycznie ustaw w UI wszystkie jako przeczytane
      setNotifications((prev) =>
        prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);

      // ✅ dociągnij stan z backendu (żeby mieć 100% pewność)
      fetchUnreadCount();
    } catch (err) {
      console.error("Błąd read-all:", err);
    }
  }, [fetchUnreadCount]);

  // ✅ usuń WSZYSTKIE przeczytane
  const deleteReadNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/notifications/read`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("DELETE read nie OK:", res.status, text);
        return;
      }

      // usuń z UI tylko te z read_at
      setNotifications((prev) => prev.filter((n) => !n.read_at));

      // unreadCount i tak dotyczy tylko nieodczytanych -> zostaje poprawny
      fetchUnreadCount();
    } catch (err) {
      console.error("Błąd usuwania przeczytanych:", err);
    }
  }, [fetchUnreadCount]);

  // ✅ usuń pojedyncze (tylko przeczytane)
  const deleteSingleNotification = useCallback(async (notificationId) => {
    try {
      const res = await fetch(`${API_URL}/notifications/${notificationId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("DELETE single nie OK:", res.status, text);
        return;
      }

      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      fetchUnreadCount();
    } catch (err) {
      console.error("Błąd usuwania powiadomienia:", err);
    }
  }, [fetchUnreadCount]);

  // ------------------------------
  // UI EVENTS
  // ------------------------------
  const handleToggleNotifications = () => {
    // zamykamy user menu, gdy otwieramy dzwonek
    setShowUserMenu(false);

    setShowNotificationsMenu((prev) => !prev);
  };

  // gdy menu powiadomień się otwiera -> pobierz listę
  useEffect(() => {
    if (showNotificationsMenu) {
      fetchNotifications();
    }
  }, [showNotificationsMenu, fetchNotifications]);

  // polling unread-count (co 45 sekund)
  useEffect(() => {
    fetchUnreadCount();

    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 45 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  // zamykanie dropdownów kliknięciem poza
  useEffect(() => {
    function handleClickOutside(e) {
      // klik poza user menu
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      // klik poza notifications menu
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotificationsMenu(false);
      }
    }

    if (showUserMenu || showNotificationsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu, showNotificationsMenu, setShowUserMenu]);

  // klik w nr_ref => oznacz jako read + przejście do zlecenia
  const handleGoToOrderFromNotification = async (notification) => {
    if (!notification) return;

    // optymistycznie:
    if (!notification.read_at) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      markNotificationRead(notification.id);
    }

    const orderId = notification?.payload?.orderId;
    if (orderId) {
      setShowNotificationsMenu(false);
      navigate(`/zlecenia/${orderId}`);
    }
  };

	const renderNotificationText = (n) => {
	  // ✅ 1) ORDER_ASSIGNED
	  if (n.type === "ORDER_ASSIGNED") {
		const p = n.payload || {};
		const fullName = `${p.assignedByFirstName || ""} ${p.assignedByLastName || ""}`.trim();
		const nrRef = p.nrRef || "(brak nr_ref)";
		const title = p.title || "";

		return (
		  <span className="notification-text">
			Użytkownik <strong>{fullName || "?"}</strong> przydzielił Cię do zlecenia:{" "}
			<button
			  type="button"
			  className="notification-link"
			  onClick={(e) => {
				e.stopPropagation();
				handleGoToOrderFromNotification(n);
			  }}
			>
			  {nrRef}
			</button>
			{title ? `, ${title}` : ""}
		  </span>
		);
	  }

	  // ✅ 2) ORDER_COMMENTED
	  if (n.type === "ORDER_COMMENTED") {
		const p = n.payload || {};

		const authorName = `${p.authorFirstName || ""} ${p.authorLastName || ""}`.trim();
		const nrRef = p.nrRef || `#${p.orderId || "?"}`;
		const preview = p.commentPreview ? `: ${p.commentPreview}` : "";

		return (
		  <span className="notification-text">
			<strong>{authorName || "Użytkownik"}</strong> napisał komentarz w zleceniu{" "}
			<button
			  type="button"
			  className="notification-link"
			  onClick={(e) => {
				e.stopPropagation();
				handleGoToOrderFromNotification(n);
			  }}
			>
			  {nrRef}
			</button>
			{preview}
		  </span>
		);
	  }

	  // ✅ 3) ORDER_STATUS_CHANGED (zmiana statusu przez przycisk)
	  if (n.type === "ORDER_STATUS_CHANGED") {
		const p = n.payload || {};
		const actorName = `${p.actorFirstName || ""} ${p.actorLastName || ""}`.trim();
		const nrRef = p.nrRef || `#${p.orderId || "?"}`;
		const newStatus = p.newStatus || "?";

		return (
		  <span className="notification-text">
			<strong>{actorName || "Użytkownik"}</strong> zmienił status zlecenia{" "}
			<button
			  type="button"
			  className="notification-link"
			  onClick={(e) => {
				e.stopPropagation();
				handleGoToOrderFromNotification(n);
			  }}
			>
			  {nrRef}
			</button>{" "}
			na <strong>"{newStatus}"</strong>
		  </span>
		);
	  }

	  // ✅ 4) ORDER_STATUS_EDITED (status zmieniony w edycji)
	  if (n.type === "ORDER_STATUS_EDITED") {
		const p = n.payload || {};
		const actorName = `${p.actorFirstName || ""} ${p.actorLastName || ""}`.trim();
		const nrRef = p.nrRef || `#${p.orderId || "?"}`;
		const newStatus = p.newStatus || "?";

		return (
		  <span className="notification-text">
			<strong>{actorName || "Użytkownik"}</strong> edytował status zlecenia{" "}
			<button
			  type="button"
			  className="notification-link"
			  onClick={(e) => {
				e.stopPropagation();
				handleGoToOrderFromNotification(n);
			  }}
			>
			  {nrRef}
			</button>{" "}
			na <strong>"{newStatus}"</strong>
		  </span>
		);
	  }

	  // fallback
	  return <span className="notification-text">Nowe powiadomienie</span>;
	};


  const hasAnyReadNotifications = notifications.some((n) => !!n.read_at);

  return (
    <header className="header">
      <div className="logo-container">
        <Link to={isAuthenticated ? "/dashboard" : "/"} className="logo-link">
          <img
            src={theme === "light" ? logoLight : logoDark}
            alt="Logo"
            className="logo"
          />
        </Link>
      </div>

      <div className="nav-right">
        {!isAuthenticated && (
          <>
            <Link to="/kontakt" className="contact-link">
              Kontakt
            </Link>
            <Link to="/login" className="contact-link">
              Zaloguj się
            </Link>
          </>
        )}

        {isAuthenticated && (
          <>
            {!isOnDashboard && (
              <Link to="/dashboard" className="nav-link">
                Dashboard
              </Link>
            )}

            <Link to="/projekty" className="nav-link">
              Projekty
            </Link>
            <Link to="/zlecenia" className="nav-link">
              Zlecenia
            </Link>
            <Link to="/zakupy" className="nav-link">
              Zakupy
            </Link>

            {currentUser && currentUser.role === "Admin" && (
              <Link to="/admin" className="nav-link">
                Panel Administratora
              </Link>
            )}

            {currentUser && currentUser.role === "Moderator" && (
              <Link to="/moderator" className="nav-link">
                Panel Moderatora
              </Link>
            )}

            {/* 🔔 POWIADOMIENIA */}
            <div className="notifications-menu-container" ref={notificationsRef}>
              <button
                className="notif-btn"
                onClick={handleToggleNotifications}
                aria-label="Powiadomienia"
                title="Powiadomienia"
              >
                <img src={getBellIcon()} alt="Powiadomienia" className="icon" />
              </button>

              {showNotificationsMenu && (
                <div className="notifications-dropdown">
                  <div className="notifications-dropdown-header">
                    <span className="notifications-title">Powiadomienia</span>

                    <div className="notifications-actions">
                      <button
                        type="button"
                        className="btn-readall"
                        onClick={markAllRead}
                        disabled={unreadCount === 0}
                        title="Oznacz wszystkie jako przeczytane"
                      >
                        Przeczytaj wszystkie
                      </button>

                      {/* ✅ Usuń przeczytane */}
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={deleteReadNotifications}
                        disabled={!hasAnyReadNotifications}
                        title="Usuń przeczytane powiadomienia"
                      >
                        <img src={deleteOn} alt="Usuń przeczytane" />
                      </button>
                    </div>
                  </div>

                  <div className="notifications-list">
                    {loadingNotifications && (
                      <div className="notifications-empty">Ładowanie…</div>
                    )}

                    {!loadingNotifications && notifications.length === 0 && (
                      <div className="notifications-empty">Brak powiadomień.</div>
                    )}

                    {!loadingNotifications &&
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`notification-item ${n.read_at ? "read" : "unread"}`}
                          onClick={() => {
                            // klik w wiersz = oznacz jako przeczytane (bez nawigacji)
                            if (!n.read_at) {
                              setNotifications((prev) =>
                                prev.map((x) =>
                                  x.id === n.id
                                    ? { ...x, read_at: new Date().toISOString() }
                                    : x
                                )
                              );
                              setUnreadCount((c) => Math.max(0, c - 1));
                              markNotificationRead(n.id);
                            }
                          }}
                        >
                          <div className="notification-row">
                            {renderNotificationText(n)}

                            {/* ✅ kosz tylko dla przeczytanych */}
                            {n.read_at && (
                              <button
                                className="notif-delete-btn"
                                title="Usuń powiadomienie"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSingleNotification(n.id);
                                }}
                              >
                                <img src={deleteOn} alt="Usuń" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Menu użytkownika */}
            <div className="user-menu-container" ref={dropdownRef}>
              <button
                className="user-btn"
                onClick={() => {
                  setShowNotificationsMenu(false);
                  setShowUserMenu((prev) => !prev);
                }}
              >
                <img
                  src={theme === "light" ? userLight : userDark}
                  alt="Ustawienia użytkownika"
                  className="icon"
                />
              </button>

              {showUserMenu && (
                <div className="user-dropdown">
                  <button className="dropdown-item" onClick={goToProfile}>
                    Mój profil
                  </button>
                  <button className="dropdown-item" onClick={goToSettings}>
                    Ustawienia
                  </button>
                  <button className="dropdown-item" onClick={onLogout}>
                    Wyloguj się
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Theme button zostawiamy jak masz teraz */}
        <button className="theme-btn" onClick={toggleTheme}>
          {theme === "light" ? (
            <img src={moonIcon} alt="Przełącz na ciemny motyw" className="icon" />
          ) : (
            <img src={sunIcon} alt="Przełącz na jasny motyw" className="icon" />
          )}
        </button>
      </div>
    </header>
  );
}

export default Header;
